import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, Navigate, NavLink } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import SEO from '../../components/SEO';
import { interviewQuestions } from '../data/interviewQuestions';
import Diagram from '../components/Diagram';
import { ArrowLeft, Clock, Share2 } from 'lucide-react';

const QuestionDetail = () => {
    const { id } = useParams();
    const [scrollProgress, setScrollProgress] = useState(0);
    const [isTocOpen, setIsTocOpen] = useState(false);

    const question = interviewQuestions.find(q => q.id === id);
    const questionIndex = interviewQuestions.findIndex(q => q.id === id);
    const prevQuestion = questionIndex > 0 ? interviewQuestions[questionIndex - 1] : null;
    const nextQuestion = questionIndex < interviewQuestions.length - 1 ? interviewQuestions[questionIndex + 1] : null;

    // Extract H2 headings for TOC
    const tocItems = useMemo(() => {
        if (!question) return [];
        const headings = [];
        const regex = /^## (.+)$/gm;
        let match;
        let index = 0;
        while ((match = regex.exec(question.content)) !== null) {
            const text = match[1].replace(/\*\*/g, '').replace(/`/g, '');
            headings.push({ id: `section-${index}`, text, index });
            index++;
        }
        return headings;
    }, [question]);

    // Scroll progress
    const handleScroll = useCallback(() => {
        const scrollTop = window.scrollY;
        const docHeight = document.documentElement.scrollHeight - window.innerHeight;
        setScrollProgress(docHeight > 0 ? (scrollTop / docHeight) * 100 : 0);
    }, []);

    useEffect(() => {
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    // Scroll to top on question change
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [id]);

    const h2IndexRef = useRef(0);
    h2IndexRef.current = 0; // Reset on every render phase to keep IDs deterministic

    const markdownComponents = useMemo(() => ({
        h2({ children }) {
            const currentIndex = h2IndexRef.current++;
            return <h2 id={`section-${currentIndex}`}>{children}</h2>;
        },
        code({ node, inline, className, children, ...props }) {
            const match = /language-(\w+)/.exec(className || '');
            if (!inline && match && match[1] === 'mermaid') {
                return <Diagram chart={String(children).replace(/\\n$/, '')} />;
            }
            return <code className={className} {...props}>{children}</code>;
        }
    }), []);

    if (!question) {
        return <Navigate to="/sysdesign/interview" replace />;
    }

    // Replace <diagram> tags with mermaid code blocks
    const processedContent = question.content.replace(/<diagram>([\s\S]*?)<\/diagram>/g, (match, p1) => {
        return `\n\`\`\`mermaid\n${p1.trim()}\n\`\`\`\n`;
    });

    // Calculate dynamic read time (200 words per minute)
    const wordCount = question.content.trim().split(/\s+/).length;
    const readTime = Math.max(1, Math.ceil(wordCount / 200));

    // Extract concept tags from summary
    const tags = question.summary
        .replace(/A classic question covering |Deep dive into |The infamous |Real-time /ig, '')
        .split(/, | and | with | using /i)
        .map(s => s.trim().replace(/\.$/, ''))
        .filter(s => s.length > 3 && s.length < 25)
        .slice(0, 4);

    return (
        <>
            <SEO
                title={`${question.title} — System Design Solution | Rastor AI`}
                description={`Complete system design solution for "${question.title}": requirements, API design, architecture with diagrams, and optimizations. ${question.summary}`}
                keywords={`${question.title}, system design, architecture diagram, API design, scalability, distributed systems`}
                canonicalUrl={`/sysdesign/interview/${id}`}
                structuredData={{
                    "@context": "https://schema.org",
                    "@type": "Article",
                    "headline": `${question.title} — System Design Solution`,
                    "description": question.summary,
                    "author": {
                        "@type": "Organization",
                        "name": "Rastor AI"
                    },
                    "publisher": {
                        "@type": "Organization",
                        "name": "Rastor AI",
                        "logo": {
                            "@type": "ImageObject",
                            "url": "https://rastorai.com/logo.png"
                        }
                    },
                    "datePublished": "2024-01-01T08:00:00+08:00",
                    "dateModified": new Date().toISOString(),
                    "mainEntityOfPage": {
                        "@type": "WebPage",
                        "@id": `https://rastorai.com/sysdesign/interview/${id}`
                    }
                }}
            />

            {/* Reading Progress Bar */}
            <div className="sd-reading-progress" style={{ width: `${scrollProgress}%` }} />

            <div className="sd-detail-layout sd-fade-in">
                {/* Main Content */}
                <div className="sd-detail-main">
                    <NavLink to="/sysdesign/interview" className="sd-back-link">
                        <ArrowLeft size={18} /> Back to Questions
                    </NavLink>

                    <article className="sd-solution-container">
                        <header className="sd-solution-header">
                            <div className="sd-header-meta">
                                <span className={`sd-difficulty-badge ${question.difficulty.toLowerCase()}`}>
                                    {question.difficulty}
                                </span>
                                <span className="sd-read-time"><Clock size={14} /> {readTime} min read</span>
                            </div>
                            <h1>{question.title}</h1>
                            <p className="sd-solution-summary">{question.summary}</p>

                            <div className="sd-topic-tags sd-mb-3">
                                {tags.map(tag => (
                                    <span key={tag} className="sd-topic-tag">{tag}</span>
                                ))}
                            </div>

                            <div className="sd-header-actions">
                                <button className="sd-btn sd-btn-outline sd-btn-sm" onClick={() => {
                                    navigator.clipboard?.writeText(window.location.href);
                                }}>
                                    <Share2 size={16} /> Share Solution
                                </button>
                            </div>
                        </header>

                        <div className="sd-solution-content sd-prose">
                            <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={markdownComponents}
                            >
                                {processedContent}
                            </ReactMarkdown>
                        </div>

                        {/* Prev/Next Navigation */}
                        <div className="sd-prev-next">
                            {prevQuestion ? (
                                <NavLink to={`/sysdesign/interview/${prevQuestion.id}`} className="sd-prev-next-card">
                                    <span className="sd-prev-next-label">← Previous</span>
                                    <span className="sd-prev-next-title">{prevQuestion.title}</span>
                                </NavLink>
                            ) : <div />}
                            {nextQuestion ? (
                                <NavLink to={`/sysdesign/interview/${nextQuestion.id}`} className="sd-prev-next-card next">
                                    <span className="sd-prev-next-label">Next →</span>
                                    <span className="sd-prev-next-title">{nextQuestion.title}</span>
                                </NavLink>
                            ) : <div />}
                        </div>
                    </article>
                </div>

                {/* TOC Sidebar */}
                {tocItems.length > 0 && (
                    <nav className="sd-detail-toc">
                        <div
                            className="sd-toc-title sd-toc-toggle"
                            onClick={() => setIsTocOpen(!isTocOpen)}
                        >
                            On This Page
                            <span className="sd-toc-toggle-icon">{isTocOpen ? '−' : '+'}</span>
                        </div>
                        <div className={`sd-toc-content ${isTocOpen ? 'open' : ''}`}>
                            {tocItems.map(item => (
                                <a
                                    key={item.id}
                                    href={`#${item.id}`}
                                    className="sd-toc-link"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setIsTocOpen(false); // Close on click
                                        document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                    }}
                                >
                                    {item.text}
                                </a>
                            ))}
                        </div>
                    </nav>
                )}
            </div>
        </>
    );
};

export default QuestionDetail;
